// The parameters one stored test case on the workout-solver test page carries, their
// defaults, and the assembly that turns one stored test case into a solver request.
//
// Why this is its own file with no Angular imports: the client unit runner is
// plain-node mocha over `src/**/*.test.ts` (see `client/package.json`), and
// importing the page itself would pull in the whole Angular graph, which does not
// load there. Keeping the assembly here is what lets
// `test-workout-solver-params.test.ts` cover it directly.
//
// Both of the page's two solve paths assemble their request through
// `buildSolverParams` below — `solveAsync` for the test the developer has open, and
// `checkOneTestAsync` for each stored case behind "Recheck all tests". They used to
// write that literal out twice, which was deferred finding `df-8aa354b56e48`.
//
// What must NOT be shared is the SOLVE itself. `solveAsync` solves twice, feeding the
// first reply's volume into the second, and throws on a failed reply, while
// `checkOneTestAsync` solves once and reports a failed reply as one test's row — which
// is what lets "Recheck all tests" list one failing test without abandoning the batch.
// Sharing the request assembly is the whole of what those two have in common.

import { StressLevel, SleepQuality, Gender } from '@cf-shared/enums';
import { TrainingLevel, TrainingLevels } from '@cf-shared/data-model/training-level';
import { WorkoutSolverParams } from '@cf-shared/workout-solver-interface';
import {
  getSimpleMuscleGrowthPreferenceTable,
  MuscleGrowthMain,
  MuscleGrowthPreferenceTable,
  OneMuscleGrowthValue,
} from '@cf-shared/data-model/muscle-growth-preference';
import { ImmutableState, DeclaredState } from '@cf-shared/data-model/user-state';
import { deepNormalize } from '@cf-shared/deep-helpers';

/**
 * The exercises the page's training-level pickers cover. {@link fillStoredTestCaseDefaults}
 * has to walk them, and the {@link TrainingLevels} interface carries no runtime list.
 *
 * DERIVED from the defaults rather than written out, because the type system cannot keep a
 * hand-written list in step: a SUBSET of the keys still satisfies `(keyof TrainingLevels)[]`,
 * so a sixth exercise added to the interface would compile cleanly here and be left out of
 * the "Undefined" rescue below — silently reinstating, for that one exercise, the defect
 * this file exists to prevent.
 */
const TRAINING_LEVEL_KEYS = Object.keys(createDefaultSolverTestParams().trainingLevels) as (keyof TrainingLevels)[];

/**
 * The training-level map as it may appear in a STORED test case, which is looser than
 * {@link TrainingLevels}: a level may be missing, because the case was saved before that
 * exercise existed, and it may be an explicit `null`, which is what the page's picker
 * stores for its "Undefined" option.
 */
type StoredTrainingLevels = Partial<Record<keyof TrainingLevels, TrainingLevel | null>>;

/**
 * One stored test case's solver inputs. This is also the shape the page's editors
 * bind to, and the shape stored as JSON in `WorkoutTestCase.testWorkoutSolverJSON`,
 * so the field names here are a stored format: renaming one makes every saved test
 * case lose that field.
 */
export type WorkoutSolverTestParams = {
  trainingLevels: TrainingLevels;
  muscleGrowth_main: MuscleGrowthMain;
  muscleGrowth: MuscleGrowthPreferenceTable;
  stressLevel: StressLevel;
  sleepQuality: SleepQuality;
  gender: Gender;
  energyBalanceFactor: number;
  trainingDays: boolean[];
};

/**
 * A fresh set of defaults, for a page that has not loaded a test case yet and as
 * the template `deepNormalize` fills an older stored test case's missing fields
 * from.
 *
 * It must be a function returning a new object rather than a shared constant,
 * because both of those callers go on to mutate what they are given: the page
 * binds its editors to it, and `deepNormalize` writes into its target.
 */
export function createDefaultSolverTestParams(): WorkoutSolverTestParams {
  return {
    trainingLevels: {
      ['$bench_press']: TrainingLevel.Intermediate,
      ['$chin_up']: TrainingLevel.Intermediate,
      ['$ohp']: TrainingLevel.Intermediate,
      ['$rdl']: TrainingLevel.Intermediate,
      ['$squat']: TrainingLevel.Intermediate,
    },
    muscleGrowth_main: MuscleGrowthMain.Balanced,
    muscleGrowth: getSimpleMuscleGrowthPreferenceTable(OneMuscleGrowthValue.Maximize),
    stressLevel: StressLevel.Average,
    sleepQuality: SleepQuality.Average,
    gender: Gender.male,
    energyBalanceFactor: 1.05,
    trainingDays: [true, true, false, true, true, false, false],
  };
}

/**
 * Fills in whatever a stored test case was saved before, IN PLACE, and hands it back.
 * Call this on the result of parsing a test case's stored JSON, before solving it or
 * binding it to the page.
 *
 * It exists because `deepNormalize` alone is not enough here. `deepNormalize` copies the
 * keys of its TEMPLATE into any field of the target that is absent — and it cannot tell an
 * absent field from one deliberately set to `null`, because it tests both with `== null`.
 * For the training levels that distinction is real: the page's picker offers an
 * "Undefined" option whose value IS `null` (`test-workout-solver.html`), so a developer can
 * choose it on purpose, and `JSON.stringify` stores it as `null`. Letting the defaults
 * overwrite that would solve the case with a training level its approval was never
 * recorded under, and report a difference that is not real — the very thing
 * df-a74d50783aa1 was about. A training level the stored case simply never had is still
 * filled from the defaults.
 */
export function fillStoredTestCaseDefaults(stored: unknown): WorkoutSolverTestParams {
  // a stored test case that does not parse into an object is deliberately NOT caught here.
  // Nothing has ever written one — the page always stores `JSON.stringify` of an object — so
  // it would mean real corruption, and quietly solving it against the defaults would report
  // a verdict that names no cause, which is the class of silent wrong answer this whole file
  // is about. Letting it fail keeps it visible. How that failure is reported today is poor,
  // and that is recorded as its own deferred finding rather than widened here
  let storedParams = stored as Partial<WorkoutSolverTestParams>;

  // for each training level this case deliberately set to "Undefined", remember it
  let storedLevels = storedParams.trainingLevels as StoredTrainingLevels | undefined;
  let deliberatelyUndefined = storedLevels
    ? TRAINING_LEVEL_KEYS.filter(key => key in storedLevels && storedLevels[key] === null)
    : [];

  deepNormalize(storedParams, createDefaultSolverTestParams());

  // and put each one back, since the defaults have just overwritten it
  let filled = storedParams as WorkoutSolverTestParams;
  let filledLevels = filled.trainingLevels as StoredTrainingLevels;
  for (let key of deliberatelyUndefined) {
    filledLevels[key] = null;
  }

  return filled;
}

/**
 * Assembles the solver request for ONE set of test-case parameters, and is the only
 * place on this page that assembles one.
 *
 * EVERY field comes off the object passed in, the two muscle-growth ones included,
 * and this function reaches no page state whatsoever. That is the invariant, and it
 * is what keeps df-a74d50783aa1 fixed: a caller holding a stored test case gets that
 * test case's own solve. Reading any field from somewhere other than the argument —
 * here or at a call site — is how that bug worked, so the page's two callers are
 * pinned against it by source-reading tests in this file's test suite.
 *
 * The casts are here because the solver only reads the handful of fields below out
 * of the two state documents, and this page has no real user to take the rest from.
 */
export function buildSolverParams(params: WorkoutSolverTestParams): WorkoutSolverParams {
  return {
    immutableState: {
      gender: params.gender,
    } as ImmutableState,
    declaredState: {
      sleepQuality: params.sleepQuality,
      stressLevel: params.stressLevel,
      trainingDays: params.trainingDays,
      muscleGrowthPreferenceMain: params.muscleGrowth_main,
      muscleGrowthPreferenceTable: params.muscleGrowth,
    } as DeclaredState,
    trainingLevels: params.trainingLevels,
    energyBalanceFactor: params.energyBalanceFactor,
  };
}
